import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel, { ValueChangedEvent } from "@widgets/SearchPanel"; // 조회조건 Panel
import { callApi, fnGetDataSet } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface

interface Props {
  Menuprops: menuInfo;
}

/** 상단 버튼(sys_headerctrl)이 달리는 cd_form. getInitForm 이 이 키로 내려준다. */
const HEADER_FORM = "grid1";

/** 버튼의 cd_param. sys_headerctrl 행·백엔드 tp 와 셋이 맞아야 한다. */
const BTN_AGGREGATE = "agg"; // 집계  → sp_cos_rollup_60
const BTN_MERGE = "merge"; // 원가반영 → sp_cos_merge_70

/**
 * 원가요소 동적 컬럼.
 * cd_elem 을 그대로 컬럼키로 쓰면 고정 컬럼명과 부딪힐 수 있어 접두어를 붙인다.
 * 컬럼은 이 접두어 기준으로 만들고 행도 같은 키로 채운다.
 */
const ELEM_PREFIX = "elem_";
/** 요소 컬럼을 끼워 넣을 위치 — 이 컬럼 바로 뒤(설계서: 수량 다음, 자체원가 앞). */
const ELEM_ANCHOR = "qt_prd";

const SN_COS_MAS_REG_008 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(), // 원가적상 — cos_rollup + cos_rollupitemdist + cos_llc
  });
  const { userInfo } = global();
  /** 기간 도움창 선택 → 자동조회. 시작/종료가 연달아 바뀌므로 마지막 것만 실행한다. */
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** 메타로 받은 원본 컬럼. 조회할 때마다 여기에 요소 컬럼을 끼워 다시 만든다. */
  const baseColsRef = useRef<any[]>([]);
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({ grid1: [] });
  const [dataSources, setDataSources] = useState<any>({ grid1: [] });
  /** 상단 버튼 메타. getInitForm 이 이미 {controls, data} 로 가공해 주므로 다시 감싸지 않는다. */
  const [headerInfo, setHeaderInfo] = useState<any>({ controls: [], data: {} });
  //#endregion

  //#region  공통 유틸
  const getSearchForm = () => searchRef.current?.getFormdata() ?? {};

  /** 원가기간 시작/종료월. SnPeriodMonthBoxPop 이 YYYYMM 을 준다. */
  const getYm = () => {
    const form = getSearchForm();
    return {
      ym_start: String(form.dt_from ?? "").substring(0, 6),
      ym_end: String(form.dt_to ?? "").substring(0, 6),
    };
  };
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        setFormData(rtn.formData); // 조회조건 초기값
        baseColsRef.current = rtn.grid?.grid1 ?? [];
        setColums({ grid1: baseColsRef.current });
        setHeaderInfo(rtn.header?.[HEADER_FORM] ?? { controls: [], data: {} });
      } catch (error) {}
    };
    fetchform();

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  //#region  원가요소 동적 컬럼
  /**
   * 2번째 결과셋(요소목록: cd_elem, nm_elem)으로 요소 컬럼을 만들어
   * 수량 컬럼 뒤에 끼운다. 요소는 회사·기간마다 다르므로 메타에 고정할 수 없다.
   */
  const buildGridColumns = (elemRows: any[]) => {
    const base = baseColsRef.current ?? [];
    if (!elemRows.length) return base;

    // 금액 서식(tp_format 등)을 맞추려고 기존 금액 컬럼을 템플릿으로 복제한다.
    const template =
      base.find((c: any) => c.cd_column === "am_prd") ??
      base.find((c: any) => c.tp_column === "SnNumberBox") ??
      base[0] ??
      {};

    const sorted = [...base].sort(
      (a: any, b: any) => Number(a.no_seq ?? 0) - Number(b.no_seq ?? 0)
    );

    const anchor = sorted.findIndex((c: any) => c.cd_column === ELEM_ANCHOR);
    const cut = anchor < 0 ? sorted.length : anchor + 1;

    const elemCols = elemRows.map((e: any) => ({
      ...template,
      cd_column: `${ELEM_PREFIX}${e.cd_elem}`,
      nm_column: e.nm_elem || e.cd_elem,
      fg_visible: "Y",
      yn_sum: "Y",
    }));

    // 순서를 바꿨으니 no_seq 도 다시 매긴다(컬럼 생성이 no_seq 를 본다).
    return [...sorted.slice(0, cut), ...elemCols, ...sorted.slice(cut)].map(
      (c: any, i: number) => ({ ...c, no_seq: i + 1 })
    );
  };

  /** 3번째 결과셋(품목·요소별 금액)을 품목 행의 요소 컬럼값으로 펼친다. */
  const buildGridRows = (itemRows: any[], elemAmRows: any[]) => {
    const rows = itemRows.map((r: any) => ({ ...r }));

    const rowByItem = new Map<string, any>();
    rows.forEach((r: any) => rowByItem.set(String(r.cd_item), r));

    elemAmRows.forEach((r: any) => {
      const row = rowByItem.get(String(r.cd_item));
      if (!row) return;
      const key = `${ELEM_PREFIX}${r.cd_elem}`;
      row[key] = Number(row[key] ?? 0) + Number(r.am_prd ?? 0);
    });

    return rows;
  };
  //#endregion

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  const onSearch = async () => {
    try {
      const form = getSearchForm();
      const { ym_start, ym_end } = getYm();

      setDataSources({ grid1: [] });

      const data: any = await fnGetDataSet({
        id: "sn_cos_mas_reg_008_01",
        params: { ...form, ym_start, ym_end },
      });

      const itemRows = data?.[0] ?? []; // 품목별 적상결과(cos_rollup)
      const elemRows = data?.[1] ?? []; // 원가요소 목록(동적 컬럼)
      const elemAmRows = data?.[2] ?? []; // 품목·요소별 금액

      // 조회 결과의 요소에 맞춰 컬럼을 다시 구성한다.
      setColums({ grid1: buildGridColumns(elemRows) });
      setDataSources({ grid1: buildGridRows(itemRows, elemAmRows) });
    } catch (error: any) {}
  };

  /**
   * 기간 도움창에서 기간을 고르면 바로 조회한다.
   * source 가 'popup' 일 때만 반응한다 — 직접 입력 중에는 조회하지 않는다.
   * (조회조건만 바뀌고 그리드는 이전 자료가 남아 있으면 오해를 부른다)
   */
  const onSearchValueChanged = (event: ValueChangedEvent) => {
    if (event.source !== "popup") return;
    if (event.colId !== "dt_from" && event.colId !== "dt_to") return;

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      searchTimerRef.current = null;
      onSearch();
    }, 0);
  };

  /** 상단 버튼(sys_headerctrl)의 cd_param 이 sender 로 넘어온다. */
  const onBtnEtcClick = async (sender: string) => {
    if (sender !== BTN_AGGREGATE && sender !== BTN_MERGE) return;

    const form = getSearchForm();
    const { ym_start, ym_end } = getYm();

    if (!form.cd_biz) {
      await showDialog("alter", "사업장을 선택해주세요.");
      return;
    }
    if (ym_start === "" || ym_end === "") {
      await showDialog("alter", "원가기간을 선택해주세요.");
      return;
    }

    try {
      if (sender === BTN_AGGREGATE) {
        // 이미 적상된 자료가 있으면 다시 묻는다. SP 가 해당 기간을 지우고 다시 만든다.
        const rows: any = await fnGetDataSet({
          id: "sn_cos_mas_reg_008_05",
          params: { cd_firm: userInfo.cd_firm, cd_biz: form.cd_biz, ym_end },
        });

        if (Number(rows?.[0]?.[0]?.cnt ?? 0) > 0) {
          const confirm = await showDialog(
            "confirm",
            "이미 처리된 내역이 있습니다. 삭제하고 다시 생성하겠습니까?"
          );
          if (!confirm) return;
        }
      } else {
        // 원가반영은 적상 결과가 화면에 있을 때만. SP 도 없으면 예외를 던지지만
        // 여기서 먼저 막아야 무엇을 먼저 해야 하는지 알 수 있다.
        if (!(dataSources.grid1?.length > 0)) {
          await showDialog("alter", "원가적상 집계를 먼저 실행해주세요.");
          return;
        }

        const confirm = await showDialog(
          "confirm",
          "원가를 반영하시겠습니까? 해당 기간의 재고평가가 다시 계산됩니다."
        );
        if (!confirm) return;
      }

      const ok = await callApi("SN_COS_MAS_REG_008_Btn", {
        tp: sender,
        cd_firm: userInfo.cd_firm,
        cd_biz: form.cd_biz,
        ym_start,
        ym_end,
      });
      // 실행만 하고 화면이 그대로면 됐는지 알 수 없다. 성공했으면 바로 다시 조회한다.
      // callApi 는 { status, data } 를 돌려준다(false 를 반환하는 경로가 없다).
      if (ok?.status) await onSearch();
    } catch (error: any) {}
  };
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onSearchValueChanged}
        ></SearchPanel>
      </div>

      <div className="flex-1 h-full min-h-0">
        <SnGrid
          ref={gridRefs.current["grid1"]}
          columnConfig={columns.grid1}
          rowData={dataSources.grid1}
          etcProps={{
            isEditable: false,
            rowSelection: "single",
            title: "원가적상",
          }}
          events={{ onBtnEtcClick }} // 집계 · 원가반영 (sys_headerctrl)
          headerInfo={headerInfo}
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_COS_MAS_REG_008;
